import { CanonicalLoad, CanonicalLoadInput, isLiveLoadSearchSocketEvent, normalizeLiveLoadExternalId } from '@numeo/types-core'
import { useCallback, useEffect, useRef } from 'react'
import { LIVE_LOAD_SEARCH_EVENT } from '../liveSearchSocketEvents'
import { closeLiveSearchQuietly } from '../closeLiveSearchQuietly'

type TabLoadsSetter = React.Dispatch<React.SetStateAction<Record<string, CanonicalLoad[]>>>

/** One socket payload as a board row. Exported because the Box Truck board applies the same deliveries. */
export const toCanonicalLoad = (load: CanonicalLoadInput, id: number, source: string): CanonicalLoad => ({
    ...load,
    id,
    externalId: normalizeLiveLoadExternalId(load.externalId, source),
    isActive: load.isActive ?? true,
    // Socket payloads from sources without a posted timestamp are still new to
    // this search. AgeDisplay reads createdAt and renders this as "0m".
    createdAt: load.createdAt ?? new Date(),
    firstSeenAt: new Date(),
    lastSeenAt: new Date(),
    pickupGeoJson: null,
    deliveryGeoJson: null,
})

// Tab IDs are persisted by Load Hub. Keeping this identifier deterministic lets the
// server atomically replace the prior subscription after a page reload instead of
// retaining another active subscription until TTL expiry.
const createClientSearchInstanceId = (tabId: string): string => `load-hub-${tabId}`

export const sameLiveLoadExternalId = (left: { externalId?: string; sourceType?: string }, externalLoadId: string, source: string): boolean =>
    left.sourceType === source && left.externalId === normalizeLiveLoadExternalId(externalLoadId, source)

/** Owns Load Hub's subscription-to-tab routing and applies socket deltas locally. */
export const useLiveLoadSearch = (setTabLoads: TabLoadsSetter) => {
    const subscriptionsByTabRef = useRef<Record<string, string>>({})
    const instanceIdsByTabRef = useRef<Record<string, string>>({})
    const closedTabsRef = useRef(new Set<string>())
    const nextSyntheticIdRef = useRef(-1_000_000)

    const getClientSearchInstanceId = useCallback((tabId: string): string => {
        closedTabsRef.current.delete(tabId)
        const existing = instanceIdsByTabRef.current[tabId]
        if (existing) return existing
        const next = createClientSearchInstanceId(tabId)
        instanceIdsByTabRef.current[tabId] = next
        return next
    }, [])

    const activateSubscription = useCallback((tabId: string, subscriptionId: string): void => {
        if (closedTabsRef.current.has(tabId)) {
            closeLiveSearchQuietly(subscriptionId, { surface: 'load_hub', tabId, reason: 'late-ack-for-closed-tab' })
            return
        }
        subscriptionsByTabRef.current[tabId] = subscriptionId
    }, [])

    const closeLiveSearchForTab = useCallback((tabId: string): void => {
        closedTabsRef.current.add(tabId)
        const subscriptionId = subscriptionsByTabRef.current[tabId]
        delete subscriptionsByTabRef.current[tabId]
        delete instanceIdsByTabRef.current[tabId]
        if (!subscriptionId) return
        closeLiveSearchQuietly(subscriptionId, { surface: 'load_hub', tabId, reason: 'tab-closed' })
    }, [])

    useEffect(() => {
        const handleEvent = (event: Event) => {
            if (!(event instanceof CustomEvent) || !isLiveLoadSearchSocketEvent(event.detail)) return
            const liveEvent = event.detail
            const tabId = Object.entries(subscriptionsByTabRef.current).find(([, subscriptionId]) => subscriptionId === liveEvent.subscriptionId)?.[0]
            if (!tabId) return

            if (liveEvent.action === 'loadCancelled') {
                setTabLoads((previous) => {
                    const loads = previous[tabId] || []
                    const next = loads.map((load) =>
                        sameLiveLoadExternalId(load, liveEvent.externalLoadId, liveEvent.source) ? { ...load, isActive: false, status: 'cancelled' } : load
                    )
                    return next.some((load, index) => load !== loads[index]) ? { ...previous, [tabId]: next } : previous
                })
                return
            }

            setTabLoads((previous) => {
                const loads = previous[tabId] || []
                if (loads.some((load) => sameLiveLoadExternalId(load, liveEvent.externalLoadId, liveEvent.source))) return previous
                const next = toCanonicalLoad(liveEvent.load, nextSyntheticIdRef.current--, liveEvent.source)
                return { ...previous, [tabId]: [...loads, next] }
            })
        }

        window.addEventListener(LIVE_LOAD_SEARCH_EVENT, handleEvent)
        return () => {
            window.removeEventListener(LIVE_LOAD_SEARCH_EVENT, handleEvent)
            const subscriptions = Object.entries(subscriptionsByTabRef.current)
            subscriptionsByTabRef.current = {}
            subscriptions.forEach(([tabId, subscriptionId]) => closeLiveSearchQuietly(subscriptionId, { surface: 'load_hub', tabId, reason: 'unmount' }))
        }
    }, [setTabLoads])

    return { activateSubscription, closeLiveSearchForTab, getClientSearchInstanceId }
}
